import type { ReactNode } from "react";
import { SocialIcon } from "./SocialIcon";
import { safeEmbedVideoUrl } from "./video";
import styles from "./SocialPost.module.css";

export type SocialMediaItem = { id: string; url: string; name: string; mimeType: string; size: number };

export function SocialPostImage({ src, alt, variant = "cover", action }: { src: string; alt: string; variant?: "cover" | "attachment"; action?: ReactNode }) {
  return <div className={styles.imageFrame}>
    {/* Native images keep this shared package independent of either app's routing and image loader. */}
    {/* eslint-disable-next-line @next/next/no-img-element */}
    <img className={variant === "cover" ? styles.coverImage : styles.attachmentImage} src={src} alt={alt} loading="lazy" decoding="async" />
    {action ? <div className={styles.imageAction}>{action}</div> : null}
  </div>;
}

export function SocialPostVideo({ src, title }: { src: string | null; title: string }) {
  const safeSrc = safeEmbedVideoUrl(src);
  return safeSrc ? <iframe className={styles.video} src={safeSrc} title={title} loading="lazy" allow="encrypted-media; fullscreen; picture-in-picture" referrerPolicy="strict-origin-when-cross-origin" allowFullScreen /> : null;
}

export function SocialPostMedia({ items, onRemove, removeLabel, sizeLabel }: { items: SocialMediaItem[]; onRemove?: (id: string) => void; removeLabel?: (name: string) => string; sizeLabel?: (bytes: number) => ReactNode }) {
  if (!items.length) return null;
  return <div className={styles.mediaList}>{items.map((item) => {
    const action = onRemove && removeLabel ? <button className={styles.remove} type="button" onClick={() => onRemove(item.id)} aria-label={removeLabel(item.name)} title={removeLabel(item.name)}><SocialIcon name="close" width="18" height="18" /></button> : null;
    return item.mimeType.startsWith("image/") ? <SocialPostImage key={item.id} src={item.url} alt={item.name} variant="attachment" action={action} /> : <div key={item.id} className={styles.attachmentRow}>
      <a className={styles.attachment} href={item.url} target="_blank" rel="noopener noreferrer"><SocialIcon name="paperclip" /><span className={styles.attachmentName}>{item.name}</span>{sizeLabel ? <span className={styles.attachmentSize}>{sizeLabel(item.size)}</span> : null}</a>
      {action}
    </div>;
  })}</div>;
}

export function SocialPostLock({ title, hint, action }: { title: string; hint: string; action: ReactNode }) {
  return <div className={styles.lockNotice}>
    <span className={styles.lockIcon}><SocialIcon name="lock" width="24" height="24" /></span>
    <strong>{title}</strong>
    <p>{hint}</p>
    {action ? <div className={styles.lockAction}>{action}</div> : null}
  </div>;
}
